import { formatPhoneNumber } from 'util/formatPhoneNumber';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, SwipeableDrawer, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import { DeleteIcon, EditSquareIcon } from 'components/icons';
import {
  deriveLeaderUiStatus,
  leaderRowActions,
  leaderStatusLabel,
  OrgMemberRow,
  LeaderUiStatus,
} from './deriveLeaderUiStatus';
import { formatLeaderDate } from './formatLeaderDates';
import { LeadershipOutlineAction } from './LeadershipTableRowActions';

const TEXT_COLOR = '#383838';
const DIVIDER_COLOR = '#DEDEDE';
const ACTIVE_COLOR = '#2E7D32';
const PENDING_COLOR = '#383838';
const COLOR_EDIT = '#23305F';
const COLOR_REMOVE = '#9B0000';

function SheetStatusPill ({ status }: { status: LeaderUiStatus }): ReactElement {
  const isActive = status === 'active';
  const pillColor = isActive ? ACTIVE_COLOR : PENDING_COLOR;
  const label = leaderStatusLabel(status);

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: '12px',
        py: '3px',
        borderRadius: '9999px',
        border: `1px solid ${pillColor}`,
        backgroundColor: 'white',
        color: pillColor,
        fontFamily: '\'Poppins\', sans-serif',
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: 'normal',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}
    >
      {label}
    </Box>
  );
}

export function LeaderDetailBottomSheet ({
  open,
  leader,
  canRemoveAdmin,
  onClose,
  onEdit,
  onRemove,
  onCancel,
  onReInvite,
}: {
  open: boolean;
  leader: OrgMemberRow | null;
  canRemoveAdmin?: boolean;
  onClose: () => void;
  onEdit: (leader: OrgMemberRow) => void;
  onRemove: (leader: OrgMemberRow) => void;
  onCancel: (leader: OrgMemberRow) => void;
  onReInvite: (leader: OrgMemberRow) => void;
}): ReactElement {
  if (!leader) return null as unknown as ReactElement;

  const ui = deriveLeaderUiStatus(leader);
  const a = leaderRowActions(ui, leader.kind, { canRemoveAdmin });
  const phone = leader.phoneNumber && formatPhoneNumber(leader.phoneNumber);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'flex-start',
          pt: '14px',
          px: '20px',
          pb: '36px',
          backgroundColor: 'white',
          overflow: 'hidden',
        }}
      >
        {/* Close (X) top-right */}
        <Box display="flex" justifyContent="flex-end" width="100%">
          <IconButton onClick={onClose} size="small" sx={{ p: 0 }}>
            <CloseIcon sx={{ fontSize: 19, color: TEXT_COLOR }} />
          </IconButton>
        </Box>

        {/* Header: Name + status pill, email, phone */}
        <Box
          display="flex"
          flexDirection="column"
          gap="3px"
          alignItems="flex-start"
          width="100%"
          overflow="hidden"
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Typography
              sx={{
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '24px',
                color: TEXT_COLOR,
                whiteSpace: 'nowrap',
              }}
            >
              {leader.firstName} {leader.lastName}
            </Typography>
            <SheetStatusPill status={ui} />
          </Box>
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '18px',
              color: TEXT_COLOR,
            }}
          >
            {leader.schoolEmail}
          </Typography>
          {phone ? (
            <Typography
              sx={{
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '18px',
                color: TEXT_COLOR,
              }}
            >
              {phone}
            </Typography>
          ) : null}
        </Box>

        {/* Divider */}
        <Box sx={{ width: '100%', height: '1px', backgroundColor: DIVIDER_COLOR }} />

        {/* ROLE */}
        <Box display="flex" flexDirection="column" gap="4px" alignItems="flex-start" width="100%" overflow="hidden">
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '10px',
              fontWeight: 600,
              lineHeight: '18px',
              textTransform: 'uppercase',
              color: TEXT_COLOR,
            }}
          >
            ROLE
          </Typography>
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '21px',
              color: TEXT_COLOR,
            }}
          >
            {leader.orgRole || '—'}
          </Typography>
        </Box>

        {/* DATE JOINED / LAST SEEN */}
        <Box display="flex" gap="16px" alignItems="flex-start" width="100%" overflow="hidden">
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap="4px" overflow="hidden">
            <Typography
              sx={{
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '10px',
                fontWeight: 600,
                lineHeight: '18px',
                textTransform: 'uppercase',
                color: TEXT_COLOR,
              }}
            >
              DATE JOINED
            </Typography>
            <Typography
              sx={{
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '21px',
                color: TEXT_COLOR,
              }}
            >
              {formatLeaderDate(leader.createdAt)}
            </Typography>
          </Box>
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap="4px" overflow="hidden">
            <Typography
              sx={{
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '10px',
                fontWeight: 600,
                lineHeight: '18px',
                textTransform: 'uppercase',
                color: TEXT_COLOR,
              }}
            >
              LAST SEEN
            </Typography>
            <Typography
              sx={{
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '21px',
                color: TEXT_COLOR,
              }}
            >
              {formatLeaderDate(leader.lastSeen)}
            </Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box display="flex" flexDirection="column" gap="10px" alignItems="flex-start" width="100%" overflow="hidden">
          {a.showEdit ? (
            <LeadershipOutlineAction
              fullWidth
              borderColor={COLOR_EDIT}
              color={COLOR_EDIT}
              icon={<EditSquareIcon />}
              onClick={() => onEdit(leader)}
            >
              Edit
            </LeadershipOutlineAction>
          ) : null}
          {a.showRemove ? (
            <LeadershipOutlineAction
              fullWidth
              borderColor={COLOR_REMOVE}
              color={COLOR_REMOVE}
              icon={<DeleteIcon />}
              onClick={() => onRemove(leader)}
            >
              Remove
            </LeadershipOutlineAction>
          ) : null}
          {a.showCancel ? (
            <LeadershipOutlineAction
              fullWidth
              borderColor={COLOR_REMOVE}
              color={COLOR_REMOVE}
              icon={<CloseIcon sx={{ fontSize: 23 }} />}
              onClick={() => onCancel(leader)}
            >
              Cancel
            </LeadershipOutlineAction>
          ) : null}
          {a.showReInvite ? (
            <LeadershipOutlineAction
              fullWidth
              borderColor={COLOR_EDIT}
              color={COLOR_EDIT}
              onClick={() => onReInvite(leader)}
            >
              Re-invite
            </LeadershipOutlineAction>
          ) : null}

          {/* Close text button */}
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                p: '15px',
                width: 150,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '\'Poppins\', sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  color: TEXT_COLOR,
                  textDecoration: 'underline',
                  whiteSpace: 'nowrap',
                }}
              >
                Close
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </SwipeableDrawer>
  );
}
